import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatErr, fmtMoney, fmtDate } from "../../api";
import { SectionTitle, StatusPill, EmptyState, RichHtml, stripHtml } from "../../components/Atoms";
import { ArrowLeft, Calendar, CheckCircle, ListChecks, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function AmbassadorProjectDetailPage() {
  const { pid } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/me/projects/${pid}/detail`); setData(data); }
    catch (e) { toast.error(formatErr(e)); nav("/me/projects"); }
  }, [pid, nav]);

  useEffect(() => { load(); }, [load]);

  const apply = async () => {
    try { await api.post("/applications", { project_id: pid }); toast.success("Applied! Admin will review."); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  if (!data) return <div className="skeleton h-64" />;

  const { project, stats, tasks } = data;
  const canApply = data.can_apply;
  const appStatus = data.application_status;

  return (
    <div className="space-y-5">
      <button onClick={() => nav(-1)} className="btn-ghost text-sm flex items-center gap-1.5 w-fit" data-testid="back-btn">
        <ArrowLeft size={14} weight="bold" /> Back
      </button>

      <div className="glass p-5 relative overflow-hidden">
        <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-gradient-to-br from-[#A3E635]/20 to-[#22D3EE]/10 blur-3xl" />
        <div className="relative">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-2xl sm:text-3xl font-black text-white">{project.title}</div>
              <div className="text-sm text-muted mt-1">{project.client_name} · {project.category || "—"}</div>
            </div>
            <StatusPill status={project.status} />
          </div>
          {project.description && <RichHtml html={project.description} className="text-sm text-muted mt-3" />}
          <div className="flex flex-wrap gap-1.5 mt-3">
            {(project.platforms || []).map(pl => (
              <span key={pl} className="text-[10px] uppercase tracking-wider font-bold text-[#22D3EE] bg-[#22D3EE]/10 border border-[#22D3EE]/30 px-2 py-0.5 rounded-full">{pl}</span>
            ))}
          </div>
          <div className="text-[10px] text-muted mt-2 flex items-center gap-1"><Calendar size={12} /> {fmtDate(project.start_date)} → {fmtDate(project.end_date)}</div>
        </div>
      </div>

      {/* Apply CTA — only when creator hasn't applied yet */}
      {(canApply || appStatus === "Pending" || appStatus === "Rejected") && (
        <div className="glass p-5 flex flex-col sm:flex-row items-center gap-4">
          <div className="flex-1">
            <div className="text-white font-bold">
              {canApply && "Ready to join this campaign?"}
              {appStatus === "Pending" && "Application sent — admin is reviewing"}
              {appStatus === "Rejected" && "Your application was not approved this time"}
            </div>
            <div className="text-xs text-muted mt-1">
              {canApply && "Review the tasks below and apply if it's a fit. You'll only see submission tools after approval."}
              {appStatus === "Pending" && "You'll get a notification when the admin decides. Hang tight."}
              {appStatus === "Rejected" && "Keep building your profile and try other campaigns."}
            </div>
          </div>
          {canApply ? (
            <button onClick={apply} className="btn-primary flex items-center gap-2" data-testid="apply-now-btn">
              <CheckCircle size={16} weight="bold" /> Apply now
            </button>
          ) : (
            <StatusPill status={appStatus === "Pending" ? "Pending" : "Rejected"} />
          )}
        </div>
      )}

      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
        <StatBox label="Total Tasks"     value={stats.total_tasks}      accent="cyan"   testid="stat-total-tasks" />
        <StatBox label="Approved"        value={stats.approved}         accent="lime"   testid="stat-approved" />
        <StatBox label="Pending"         value={stats.pending ?? 0}     accent="amber"  testid="stat-pending" />
        <StatBox label="Rejected"        value={stats.rejected}         accent="pink"   testid="stat-rejected" />
        <StatBox label="Potential"       value={fmtMoney(stats.potential_earnings)} accent="purple" testid="stat-potential" />
        <StatBox label="Approved Earnings" value={fmtMoney(stats.approved_earnings)}  accent="green"  testid="stat-approved-earn" />
        <StatBox label="Bonus"           value={fmtMoney(stats.bonus)}              accent="cyan"   testid="stat-bonus" />
      </div>

      {/* Task list — each card now opens a dedicated Task Detail page */}
      <div className="glass p-5">
        <div className="text-white font-bold mb-3 flex items-center gap-2"><ListChecks size={18} weight="bold" /> Tasks</div>
        {tasks.length === 0 ? <EmptyState title="No tasks yet" /> : (
          <div className="grid sm:grid-cols-2 gap-3">
            {tasks.map(t => {
              const reward = (t.reward_amount || 0) + (t.bonus_amount || 0);
              return (
                <Link key={t.id}
                      to={`/me/projects/${pid}/tasks/${t.id}`}
                      data-testid={`task-row-${t.id}`}
                      className="block p-4 rounded-2xl bg-white/3 border border-white/5 hover:bg-white/5 hover:border-[#22D3EE]/30 transition">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="text-white font-semibold flex-1 min-w-0 truncate">{t.title}</div>
                    <StatusPill status={t.submission?.status || t.status} />
                  </div>
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    {((t.platforms && t.platforms.length > 0) ? t.platforms : (t.platform ? [t.platform] : [])).map(pl => (
                      <span key={pl} className="text-[10px] uppercase tracking-wider font-bold text-[#22D3EE] bg-[#22D3EE]/10 border border-[#22D3EE]/30 px-2 py-0.5 rounded-full whitespace-nowrap">{pl}</span>
                    ))}
                    <span className="text-[10px] uppercase tracking-wider font-bold text-[#A3E635] bg-[#A3E635]/10 border border-[#A3E635]/30 px-2 py-0.5 rounded-full">{fmtMoney(reward)}</span>
                    {t.bonus_amount > 0 && <span className="text-[10px] uppercase tracking-wider font-bold text-[#EC4899] bg-[#EC4899]/10 border border-[#EC4899]/30 px-2 py-0.5 rounded-full">+ {fmtMoney(t.bonus_amount)} bonus</span>}
                  </div>
                  {t.instructions && <div className="text-xs text-muted line-clamp-2">{stripHtml(t.instructions)}</div>}
                  <div className="flex items-center justify-between mt-2">
                    <div className="text-[10px] text-muted flex items-center gap-1">
                      {t.deadline && <><Calendar size={12} /> Due {fmtDate(t.deadline)}</>}
                    </div>
                    <div className="text-[10px] text-[#22D3EE] flex items-center gap-1 font-bold">Open task <ArrowRight size={12} weight="bold" /></div>
                  </div>
                  {t.submission?.feedback && <div className="text-xs text-[#F59E0B] mt-2">Feedback: {t.submission.feedback}</div>}
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

const StatBox = ({ label, value, accent = "purple", testid }) => {
  const c = { cyan:"#22D3EE", lime:"#A3E635", amber:"#F59E0B", pink:"#EC4899", purple:"#8B5CF6", green:"#10B981" }[accent] || "#8B5CF6";
  return (
    <div className="glass p-3 sm:p-4 relative overflow-hidden" data-testid={testid}>
      <div className="absolute -top-6 -right-6 w-20 h-20 rounded-full blur-2xl" style={{ background: `${c}33` }} />
      <div className="relative">
        <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-muted">{label}</div>
        <div className="text-xl sm:text-2xl font-black text-white mt-1">{value}</div>
      </div>
    </div>
  );
};
